import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './style.less';
import log from 'shared/utils/log';
import { CallTaskStatus, CompletedCallTaskStates } from 'types/outbound';
import { postFinalCallReport } from 'shared/api';
import { useAppSelector } from 'shared/store';
import { Icon } from 'shared/components/Icon';
import { Form, Input, Select, message } from 'antd';
import { Button } from 'shared/components/Button';
import {
  selectCallDispositions,
  selectCallSentiments,
} from 'shared/store/outbound-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import clsx from 'clsx';

export const ConnectedCallLog: React.FC<{
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
  submitted?: string | null | undefined;
  setSubmitted: React.Dispatch<React.SetStateAction<string | null | undefined>>;
}> = ({ setRefreshKey, submitted, setSubmitted }) => {
  const { detailTask: dialTask } = useAppSelector(s => s.outbound);
  const user = useAppSelector(selectActiveUser);
  const dispositions = useAppSelector(selectCallDispositions);
  const sentiments = useAppSelector(selectCallSentiments);
  const [selectedDispositionId, setSelectedDispositionId] = useState<string | null>(null);
  const [selectedSentimentId, setSelectedSentimentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { transcriptions } = useAppSelector(s => s.transcription);

  const aiSummary = useMemo(() => {
    if (!dialTask?.id) return '';
    return transcriptions?.[dialTask?.id]?.summary ?? '';
  }, [transcriptions, dialTask?.id]);

  useEffect(() => {
    if (dialTask?.callSid !== submitted) {
      setSubmitted(null);
      setError(null);
    }
  }, [dialTask, setSubmitted, submitted]);

  const handleSubmit = useCallback(async () => {
    if (!selectedDispositionId || !dialTask?.id) {
      const msg =
        'Must have a connected call and select a call disposition before submitting call report.';
      log.error(msg);
      setError(msg);
      return;
    }

    setLoading(true);

    try {
      await postFinalCallReport(dialTask?.id as string, {
        notes,
        dispositionId: selectedDispositionId,
        sentimentId: selectedSentimentId,
      });

      setSubmitted(dialTask.callSid);
      setLoading(false);
      setNotes('');
      setSelectedDispositionId(null);
      setSelectedSentimentId(null);

      setRefreshKey(k => k + 1);
    } catch (err) {
      const msg = 'Error submitting call feedback';
      log.error(msg, err);
      message.error(msg);
      setError(msg);
      setLoading(false);

      return;
    }
  }, [
    dialTask?.callSid,
    dialTask?.id,
    notes,
    selectedDispositionId,
    selectedSentimentId,
    setRefreshKey,
    setSubmitted,
  ]);

  const showAiSummary = useMemo(() => {
    return (
      !!dialTask?.id &&
      user?.settings.outboundCallRecordingEnabled &&
      !user.settings.outboundCallRecordingRepOnly
    );
  }, [
    dialTask?.id,
    user?.settings.outboundCallRecordingEnabled,
    user?.settings.outboundCallRecordingRepOnly,
  ]);

  if (!dialTask || dialTask.status === CallTaskStatus.PENDING) {
    return <></>;
  }

  if (!dispositions.length) {
    return <></>;
  }

  return (
    <div className="ConnectedCallLog">
      <div className="ConnectedCallLog__header">
        <Icon icon="note" />
        Call Notes
      </div>
      <div className="ConnectedCallLog__body">
        <Form className="ConnectedCallLog__form" onFinish={() => {}}>
          <Input.TextArea
            name=""
            className="ConnectedCallLog__notes"
            rows={6}
            value={notes}
            placeholder={'Type a note...'}
            onChange={e => setNotes(e.target.value)}
          />

          {showAiSummary && (
            <label className="ConnectedCallLog__label">
              <span>
                <Icon icon="stars" width={20} height={20} />
                AI Summary
              </span>
              <Input.TextArea
                name=""
                placeholder="Waiting for AI summary..."
                className="ConnectedCallLog__notes"
                rows={6}
                value={aiSummary}
                disabled
              />
            </label>
          )}

          <label className="ConnectedCallLog__disposition-label">
            Call disposition
            <Select
              className="ConnectedCallLog__disposition"
              placeholder={'Choose disposition'}
              onChange={d => setSelectedDispositionId(d)}
              value={selectedDispositionId || undefined}
            >
              {dispositions.map(d => {
                return (
                  <Select.Option key={d.id} value={d.id}>
                    {d.label}
                  </Select.Option>
                );
              })}
            </Select>
          </label>

          {!!sentiments.length && (
            <label className="ConnectedCallLog__disposition-label">
              Call sentiment
              <Select
                className="ConnectedCallLog__disposition"
                placeholder={'Choose sentiment'}
                onChange={s => {
                  setSelectedSentimentId(s);
                }}
                value={selectedSentimentId || undefined}
              >
                {sentiments.map(s => {
                  return (
                    <Select.Option key={s.id} value={s.id}>
                      {s.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </label>
          )}

          {!!error && (
            <div className="ConnectedCallLog__status ConnectedCallLog__status--negative">
              {error}
            </div>
          )}
          <Button
            className={clsx(
              'ConnectedCallLog__submit',
              submitted && 'ConnectedCallLog__submit--submitted',
            )}
            htmlType="submit"
            type="primary"
            size="middle"
            onClick={handleSubmit}
            loading={loading}
            disabled={
              !selectedDispositionId ||
              !!submitted ||
              !CompletedCallTaskStates.includes(dialTask.status) ||
              loading
            }
          >
            {submitted ? (
              <>
                <Icon width={16} height={16} icon="check" />
                Call logged
              </>
            ) : (
              'Log Call'
            )}
          </Button>
        </Form>
      </div>
    </div>
  );
};
